import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faSave, faTimes, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import AdminHeader from '../layout/AdminHeader';
import AdminNavigation from '../layout/AdminNavigation';
import { SocialSkill } from '../../../types';
import { getSocialSkills, createSocialSkill, updateSocialSkill, deleteSocialSkill } from '../../../api/socialSkillApi';
import { getSortIcon, containsEmoji } from '../../../utils/Utils';
import './AdminSocialSkillConfig.css';

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

const AdminSocialSkillConfig: React.FC = () => {
  const [socialSkillQuestions, setSocialSkillQuestions] = useState<Array<SocialSkill>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<string>('');
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [newQuestion, setNewQuestion] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'order', direction: 'asc' });
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    fetchSocialSkills();
  }, []);

  const fetchSocialSkills = async (): Promise<void> => {
    try {
      setLoading(true);
      const socialSkillData = await getSocialSkills();
      if (socialSkillData && socialSkillData.length > 0) {
        setSocialSkillQuestions(socialSkillData);
      }
      setError('');
    } catch (error: any) {
      console.error('Error fetching social skills:', error);
      setError('選択式質問の取得中にエラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (socialSkill: SocialSkill): void => {
    setEditingId(socialSkill.id);
    setEditingQuestion(socialSkill.text || '');
  };

  const validateSocialSkillText = (text: string, excludeId?: number): string | null => {
    if (!text.trim()) {
      return '質問内容を入力してください。';
    }

    if (containsEmoji(text)) {
      return '絵文字は使用できません。';
    }

    const trimmedText = text.trim();
    const isDuplicate = socialSkillQuestions.some(socialSkill => {
      const socialSkillText = (socialSkill.text || '').trim();
      return socialSkillText === trimmedText && socialSkill.id !== excludeId;
    });

    if (isDuplicate) {
      return 'この質問内容は既に登録されています。';
    }

    return null;
  };

  const handleCancelEdit = (): void => {
    setEditingId(null);
    setEditingQuestion('');
    setValidationErrors({});
    setIsAdding(false);
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editingId) return;

    const validationError = validateSocialSkillText(editingQuestion, editingId);
    if (validationError) {
      setValidationErrors({ [`edit_${editingId}`]: validationError });
      return;
    }

    try {
      setIsSubmitting(true);
      const socialSkill = socialSkillQuestions.find(s => s.id === editingId);
      if (!socialSkill) return;

      const updatedSocialSkill = await updateSocialSkill(editingId, {
        order: socialSkill.order,
        text: editingQuestion.trim()
      });

      setSocialSkillQuestions(prevSocialSkills => 
        prevSocialSkills.map(socialSkill => 
          socialSkill.id === editingId 
            ? { ...socialSkill, text: updatedSocialSkill.text }
            : socialSkill
        )
      );

      setEditingId(null);
      setEditingQuestion('');
      setValidationErrors({});
    } catch (error: any) {
      console.error('Error updating social skill:', error);
      if (error.response?.data?.errors?.text) {
        setValidationErrors({ [`edit_${editingId}`]: error.response.data.errors.text[0] });
      } else {
        alert('質問の更新が失敗しました。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddClick = (): void => {
    setIsAdding(true);
    setNewQuestion('');
    setEditingId(null);
  };

  const handleCancelAdd = (): void => {
    setIsAdding(false);
    setNewQuestion('');
    setValidationErrors({});
  };

  const handleSaveAdd = async (): Promise<void> => {
    const validationError = validateSocialSkillText(newQuestion);
    if (validationError) {
      setValidationErrors({ 'new_question': validationError });
      return;
    }

    if (socialSkillQuestions.length >= 15) {
      alert('質問は最大15件まで登録できます。');
      return;
    }

    try {
      setIsSubmitting(true);
      const nextOrder = Math.max(...socialSkillQuestions.map(socialSkill => socialSkill.order), 0) + 1;

      const newSocialSkill = await createSocialSkill({
        order: nextOrder,
        text: newQuestion.trim()
      });

      setSocialSkillQuestions(prevSocialSkills => [
        ...prevSocialSkills,
        { ...newSocialSkill }
      ]);

      setIsAdding(false);
      setNewQuestion('');
      setValidationErrors({});
    } catch (error: any) {
      console.error('Error creating social skill:', error);
      if (error.response?.data?.errors?.text) {
        setValidationErrors({ 'new_question': error.response.data.errors.text[0] });
      } else if (error.response?.status === 422) {
        alert(error.response.data.message || '質問は最大15件まで登録できます');
      } else {
        alert('質問の作成が失敗しました。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number): Promise<void> => {
    if (!window.confirm(`Q${id}の質問を削除してもよろしいですか？`)) {
      return;
    }

    try {
      setIsSubmitting(true);
      await deleteSocialSkill(id);
      
      await fetchSocialSkills();
    } catch (error: any) {
      console.error('Error deleting social skills:', error);
      if (error.response?.status === 422) {
        alert('質問は最低1件必要です');
      } else {
        alert('質問の削除が失敗しました。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (key: string): void => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedSocialSkills = React.useMemo(() => {
    let filtered = socialSkillQuestions.filter(socialSkill =>
      (socialSkill.text || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortConfig.key) {
        case 'order':
          aValue = a.order;
          bValue = b.order;
          break;
        case 'question':
          aValue = (a.text || '').toLowerCase();
          bValue = (b.text || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [socialSkillQuestions, searchTerm, sortConfig]);

  if (loading) {
    return (
      <div className="dashboard-container">
        <AdminHeader />

        <Container>
          <AdminNavigation />
          <div className="blank-form-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">情報を読み込んでいます...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AdminHeader />

      <Container>

        <div className="navigation-container">
          <AdminNavigation />
          <div className="search-container">
            <div className="search-input-container">
              <Form>
                <Form.Control
                  type="text"
                  placeholder="質問内容で検索"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="search-input"
                />
              </Form>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <div className='reference-check-text'>
              選択式質問設定
            </div>

            <div className="social-skill-info mb-3">
              <p className="text-muted">
                回答フォームに表示する選択式質問を管理します。最大15件まで登録可能です。
              </p>
            </div>
          </div>

          <Button 
            variant="outline-success" 
            size="sm" 
            onClick={handleAddClick}
            disabled={isSubmitting || socialSkillQuestions.length >= 15 || isAdding || editingId !== null}
            className="detail-button"
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            質問追加
          </Button>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">{error}</Alert>
        )}

        <div className="candidates-table-container">
          <table className="candidates-table social-skill-config-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('order')} className="sortable text-center" style={{ width: '80px' }}>
                  順序 {getSortIcon('order', sortConfig)}
                </th>
                <th onClick={() => handleSort('question')} className="sortable text-left">
                  質問内容 {getSortIcon('question', sortConfig)}
                </th>
                <th className="text-center" style={{ width: '150px' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {sortedSocialSkills.map((socialSkill) => (
                <tr key={socialSkill.id}>
                  <td className="text-center">
                    <span className="question-index">Q{socialSkill.order}.</span>
                  </td>
                  <td className="text-left">
                    {editingId === socialSkill.id ? (
                      <div className="question-input">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <Form.Control
                              type="text"
                              value={editingQuestion}
                              onChange={(e) => {
                                setEditingQuestion(e.target.value);
                                if (validationErrors[`edit_${socialSkill.id}`]) {
                                  setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[`edit_${socialSkill.id}`];
                                    return newErrors;
                                  });
                                }
                              }}
                              placeholder="質問内容を入力してください"
                              maxLength={500}
                              disabled={isSubmitting}
                              isInvalid={!!validationErrors[`edit_${socialSkill.id}`]}
                            />
                            {validationErrors[`edit_${socialSkill.id}`] && (
                              <Form.Control.Feedback type="invalid">
                                {validationErrors[`edit_${socialSkill.id}`]}
                              </Form.Control.Feedback>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="question-text mb-0">{socialSkill.text || ''}</p>
                    )}
                  </td>
                  <td className="text-center">
                    {editingId === socialSkill.id ? (
                      <div>
                        <Button
                          className="detail-button me-2"
                          onClick={handleSaveEdit}
                          disabled={isSubmitting}
                        >
                          <FontAwesomeIcon icon={faSave} className="me-1" />
                          保存
                        </Button>
                        <Button
                          className="cancel-button-icon"
                          onClick={handleCancelEdit}
                          disabled={isSubmitting}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Button
                          className="detail-button me-2"
                          onClick={() => handleEditClick(socialSkill)}
                          disabled={editingId !== null || isSubmitting || isAdding}
                        >
                          <FontAwesomeIcon icon={faEdit} className="me-1" />
                          編集
                        </Button>
                        <Button
                          className="delete-button-icon"
                          onClick={() => handleDelete(socialSkill.id)}
                          disabled={editingId !== null || isSubmitting || socialSkillQuestions.length <= 1 || isAdding}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {isAdding && (
                <tr>
                  <td className="text-center">
                    <span className="question-index">Q{socialSkillQuestions.length + 1}.</span>
                  </td>
                  <td className="text-left">
                    <div className="question-input">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <Form.Control
                            type="text"
                            value={newQuestion}
                            onChange={(e) => {
                              setNewQuestion(e.target.value);
                              if (validationErrors['new_question']) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors['new_question'];
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="質問内容を入力してください"
                            maxLength={500}
                            disabled={isSubmitting}
                            isInvalid={!!validationErrors['new_question']}
                          />
                          {validationErrors['new_question'] && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors['new_question']}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-center">
                    <div>
                      <Button
                        className="detail-button me-2"
                        onClick={handleSaveAdd}
                        disabled={isSubmitting}
                      >
                        <FontAwesomeIcon icon={faSave} className="me-1" />
                        保存
                      </Button>
                      <Button
                        className="cancel-button-icon"
                        onClick={handleCancelAdd}
                        disabled={isSubmitting}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
              {sortedSocialSkills.length === 0 && !isAdding && (
                <tr>
                  <td colSpan={3} className="text-center">
                    {searchTerm ? '検索条件に一致する質問が見つかりません。' : '質問が登録されていません。'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </Container>
    </div>
  );
};

export default AdminSocialSkillConfig;
